import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';

import {fromEvent} from "rxjs";
import { AnimationOptions } from 'ngx-lottie';

import packageJson from "../../../../../package.json";

import {AuthenticateUserRequest} from "@commons/models/remote.request/AuthenticateUserRequest";
import { environment } from '@src/environments/environment';

import {NewNavigationService} from "@shared/services/new-navigation.service";
import {BaseComponent} from "@shared/components/base-component";
import {PermissionService} from '@shared/services/permission.service';
import {UserDatabaseModel} from '@shared/models/user-database.model';
import { AuthenticationService } from '@shared/services/authentication/authentication.service';
import { SettingsService } from '@shared/services/settings.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent extends BaseComponent implements OnInit, OnDestroy {

  public readonly version: string = packageJson.version;
  public readonly isChristmasEvent: boolean = environment.isChristmasEvent;
  public readonly chrismatHatConfigLottie: AnimationOptions = {
    path: "/assets/lottie/christmas-hat.json"
  };
  public readonly christmasBaublesConfigLottie: AnimationOptions = {
    path: "/assets/lottie/Animation1734601331861.json"
  };

  showGeneralError = false;
  isLoading = false;
  isRawPasswordVisible = false;
  inputType = 'password';

  emailCtrl = new FormControl('', [Validators.required, Validators.email]);
  passwordCtrl = new FormControl('', [Validators.required]);
  stayLoggedInCtrl = new FormControl(false);

  form = this.formBuilder.group({
    email: this.emailCtrl,
    password: this.passwordCtrl,
    stayLoggedIn: this.stayLoggedInCtrl,
  });

  get isVisibleShowPasswordButton(): boolean {
    return this.passwordCtrl.value != null && this.passwordCtrl.value.length > 0;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private navigationService: NewNavigationService,
    private newNavigationService: NewNavigationService,
    private settingsService: SettingsService,
    private permissionService: PermissionService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToKeyboardEnter();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  onNavigateToCreateAccount() {
    this.navigationService.toCreateAccount();
  }

  onNavigateToResetPassword() {
    this.navigationService.toResetPassword();
  }

  onShowPasswords() {
    this.isRawPasswordVisible = true;
    this.inputType = 'text';
  }

  onHidePasswords() {
    this.isRawPasswordVisible = false;
    this.inputType = 'password';
  }

  onLogin() {
    if (this.isLoading)
      return;

    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.showGeneralError = true;
      return;
    }

    this.isLoading = true;
    this.showGeneralError = false;

    const request = this.getAuthenticateUserRequest();
    this.authenticationService.authenticate(request).subscribe({
      next: () => {
        this.afterAuthentication(request);
      },
      error: () => this.afterAuthenticationError()
    });
  }

  private getAuthenticateUserRequest(): AuthenticateUserRequest {
    const request = {} as AuthenticateUserRequest;
    request.Login = this.emailCtrl.value!;
    request.Password = this.passwordCtrl.value!;
    request.RememberMeEnabled = this.stayLoggedInCtrl.value!;

    return request;
  }

  private afterAuthentication(request: AuthenticateUserRequest) {
    this.settingsService.setLoggedUserEmail(request.Login);

    if (this.authenticationService.canSelectDatabase) {
      this.newNavigationService.toSelectDatabase();
    } else {
      request.DB = this.authenticationService.currentClient;
      request.SessionId = this.authenticationService.sessionId;

      if (!this.permissionService.hasClientSelection()) {
        const model = {} as UserDatabaseModel;
        model.user = request.Login.toLowerCase();
        model.database = request.DB;
      }

      this.authenticationService.authenticate(request).subscribe({
        next: () => {
          this.newNavigationService.toMainPanel();
        },
        error: () => this.afterAuthenticationError()
      });
    }
  }

  private afterAuthenticationError() {
    this.passwordCtrl.setValue(null);
    this.showGeneralError = true;
    this.isLoading = false;
  }

  private subscribeToKeyboardEnter() {
    this.addToUnsubscribe(fromEvent(document, 'keyup').subscribe(e => {
      const event = e as KeyboardEvent;
      if (event.key === 'Enter') {
        this.onLogin();
      }
    }));
  }

}
